/**
 * The navigation registry: sections and pages, the permission bits each needs, and
 * whether the page is rebuilt yet. Pages that aren't ready render a "coming soon"
 * screen linking to the same page in the current backoffice (`legacyPath`).
 *
 * Ids are a contract: brand-pack overlays (GET /api/config navigation.items) target
 * them to relabel, reorder, re-icon or hide entries. Keep every id in LEGACY_OVERLAY_IDS.
 */
import {
  AvatarIcon,
  BarChartIcon,
  BellIcon,
  CounterClockwiseClockIcon,
  CubeIcon,
  DashboardIcon,
  DesktopIcon,
  DrawingPinIcon,
  EnterIcon,
  FileTextIcon,
  GearIcon,
  GlobeIcon,
  LayersIcon,
  LightningBoltIcon,
  LockClosedIcon,
  MagicWandIcon,
  MixerHorizontalIcon,
  PersonIcon,
  PieChartIcon,
  ReaderIcon,
  RocketIcon,
  SliderIcon,
  StarIcon,
  TimerIcon,
  TokensIcon,
} from '@radix-ui/react-icons';
import type { Permission } from '@/auth/permissions';
import type { BrandNavItemOverlay } from './runtime-config';
import { OVERLAY_ICONS, type NavIcon } from './nav-icons';

export interface NavItem {
  id: string;
  label: string;
  /** Router path. */
  to: string;
  /** Every top-level item has its own icon; sub-items don't show one. */
  icon?: NavIcon;
  permissions: Permission[];
  /** Root-only pages (the server adds CrossOperatorGuard). */
  superAdminOnly?: boolean;
  /** Rebuilt in web/. When false, the path shows a placeholder with a link to `legacyPath`. */
  ready: boolean;
  /** Same page in the legacy UI (served at /), while it still exists. */
  legacyPath?: string;
  /**
   * Variants of this page, nested under it in the sidebar (e.g. Rounds → Live). The parent stays a
   * real page: its own list.
   */
  children?: NavItem[];
}

export interface NavSection {
  id: string;
  label: string;
  icon: NavIcon;
  items: NavItem[];
}

// Every page is rebuilt now; a future unbuilt entry is `{ ...item, ready: false }`.
const ready = (item: Omit<NavItem, 'ready'>): NavItem => ({ ...item, ready: true });

export const NAV: NavSection[] = [
  {
    id: 'dashboard',
    label: 'Analytics',
    icon: BarChartIcon,
    items: [
      ready({
        id: 'analytics.overview',
        label: 'Overview',
        icon: DashboardIcon,
        to: '/analytics',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/dashboard',
      }),
      ready({
        id: 'analytics.games',
        label: 'Game performance',
        icon: BarChartIcon,
        to: '/analytics/games',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/dashboard',
      }),
      ready({
        id: 'analytics.operators',
        label: 'Operator performance',
        icon: PieChartIcon,
        to: '/analytics/operators',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/dashboard',
      }),
      ready({
        id: 'analytics.players',
        label: 'Players',
        icon: AvatarIcon,
        to: '/analytics/players',
        permissions: ['VIEW_LIST_GAMES'],
        children: [
          ready({
            id: 'analytics.players.retention',
            label: 'Retention',
            to: '/analytics/players/retention',
            permissions: ['VIEW_LIST_GAMES'],
          }),
          ready({
            id: 'analytics.players.funnel',
            label: 'Funnel',
            to: '/analytics/players/funnel',
            permissions: ['VIEW_LIST_GAMES'],
          }),
          ready({
            id: 'analytics.players.value',
            label: 'Value & sessions',
            to: '/analytics/players/value',
            permissions: ['VIEW_LIST_GAMES'],
          }),
          ready({
            id: 'analytics.players.risk',
            label: 'Top players & risk',
            to: '/analytics/players/risk',
            permissions: ['VIEW_LIST_GAMES', 'VIEW_LIST_PLAYERS'],
          }),
        ],
      }),
      ready({
        id: 'analytics.explore',
        label: 'Explore',
        icon: MixerHorizontalIcon,
        to: '/analytics/explore',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/dashboard',
      }),
      ready({
        id: 'analytics.reports',
        label: 'Reports',
        icon: FileTextIcon,
        to: '/analytics/reports',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/dashboard',
      }),
      ready({
        id: 'analytics.alerts',
        label: 'Alerts',
        icon: BellIcon,
        to: '/analytics/alerts',
        permissions: ['VIEW_LIST_GAMES'],
      }),
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    icon: CubeIcon,
    items: [
      ready({
        id: 'games',
        label: 'Games',
        icon: CubeIcon,
        to: '/games',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/games',
      }),
      ready({
        id: 'configs',
        label: 'Configs',
        icon: SliderIcon,
        to: '/configs',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/configs',
        children: [
          ready({
            id: 'inspector',
            label: 'Effective config',
            to: '/configs/effective',
            permissions: ['VIEW_LIST_GAMES'],
            legacyPath: '/inspector',
          }),
        ],
      }),
      ready({
        id: 'currencies',
        label: 'Currencies',
        icon: TokensIcon,
        to: '/currencies',
        permissions: ['VIEW_LIST_CURRENCIES'],
        legacyPath: '/currencies',
      }),
    ],
  },
  {
    id: 'partners',
    label: 'Partners',
    icon: GlobeIcon,
    items: [
      ready({
        id: 'operators',
        label: 'Operators',
        icon: GlobeIcon,
        to: '/operators',
        permissions: ['VIEW_LIST_OPERATORS'],
        legacyPath: '/operators',
      }),
      ready({
        id: 'brands',
        label: 'Brands',
        icon: LayersIcon,
        to: '/brands',
        permissions: ['VIEW_LIST_BRANDS'],
        legacyPath: '/brands',
      }),
    ],
  },
  {
    id: 'promotions',
    label: 'Promotions',
    icon: RocketIcon,
    items: [
      ready({
        id: 'promotions.campaigns',
        label: 'Campaigns',
        icon: RocketIcon,
        to: '/campaigns',
        permissions: ['VIEW_LIST_PROMOTIONS'],
        legacyPath: '/campaigns',
      }),
      ready({
        id: 'promotions.activated-awards',
        label: 'Awards',
        icon: StarIcon,
        to: '/awards/active',
        permissions: ['VIEW_LIST_PROMOTIONS'],
        legacyPath: '/activated-awards',
        children: [
          ready({
            id: 'promotions.finished-awards',
            label: 'Finished',
            to: '/awards/finished',
            permissions: ['VIEW_LIST_PROMOTIONS'],
            legacyPath: '/finished-awards',
          }),
        ],
      }),
    ],
  },
  {
    id: 'players-rounds',
    label: 'Players & rounds',
    icon: PersonIcon,
    items: [
      ready({
        id: 'players',
        label: 'Players',
        icon: PersonIcon,
        to: '/players',
        permissions: ['VIEW_LIST_PLAYERS'],
        legacyPath: '/players',
      }),
      ready({
        id: 'rounds',
        label: 'Rounds',
        icon: CounterClockwiseClockIcon,
        to: '/rounds',
        permissions: ['VIEW_LIST_GAMES'],
        legacyPath: '/rounds',
        children: [ready({ id: 'rounds.live', label: 'Live', to: '/rounds/live', permissions: ['VIEW_LIST_GAMES'] })],
      }),
      ready({
        id: 'sessions',
        label: 'Sessions',
        icon: TimerIcon,
        to: '/sessions',
        permissions: ['VIEW_LIST_PLAYERS'],
      }),
      ready({
        id: 'temporarly-saved-data',
        label: 'Pins',
        icon: DrawingPinIcon,
        to: '/pins',
        permissions: ['VIEW_LIST_PLAYERS'],
        legacyPath: '/temporarly-saved-data',
      }),
    ],
  },
  {
    id: 'jackpots',
    label: 'Jackpots & bonus',
    icon: LightningBoltIcon,
    items: [
      ready({
        id: 'jackpots.progressive',
        label: 'Progressive jackpots',
        icon: LightningBoltIcon,
        to: '/jackpots',
        permissions: ['VIEW_LIST_GAMES'],
      }),
      ready({
        id: 'bonus-games',
        label: 'Bonus games',
        icon: MagicWandIcon,
        to: '/bonus-games',
        permissions: ['VIEW_LIST_GAMES'],
      }),
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: GearIcon,
    items: [
      ready({
        id: 'backoffice-users',
        label: 'Users & access',
        icon: LockClosedIcon,
        to: '/users',
        permissions: ['VIEW_LIST_USERS'],
        legacyPath: '/backoffice-users',
      }),
      ready({
        id: 'demo-area',
        label: 'Demo area',
        icon: DesktopIcon,
        to: '/demo-area',
        permissions: ['VIEW_LIST_DEMOAREA'],
        superAdminOnly: true,
        legacyPath: '/demo-area',
      }),
      ready({ id: 'audit-log', label: 'Audit log', icon: ReaderIcon, to: '/audit', permissions: ['VIEW_LIST_USERS'] }),
      ready({
        id: 'access-log',
        label: 'Access log',
        icon: EnterIcon,
        to: '/access-log',
        permissions: ['VIEW_LIST_PLAYERS'],
      }),
      // GET /system/1 needs the single-operator bit; the list endpoint is broken (api-traps #4).
      ready({ id: 'system', label: 'System', icon: GearIcon, to: '/system', permissions: ['VIEW_SINGLE_OPERATORS'] }),
    ],
  },
];

/** Palette actions that brand overlays may also target (legacy id `clear-cache`). */
export const NAV_ACTION_IDS = ['clear-cache'] as const;

/** Every id the legacy UI exposed to brand overlays. They must stay resolvable. */
export const LEGACY_OVERLAY_IDS = [
  'dashboard',
  'operators',
  'brands',
  'configs',
  'games',
  'rounds',
  'players',
  'currencies',
  'promotions',
  'promotions.activated-awards',
  'promotions.campaigns',
  'promotions.finished-awards',
  'demo-area',
  'backoffice-users',
  'inspector',
  'temporarly-saved-data',
  'clear-cache',
] as const;

export interface VisibleNavItem extends Omit<NavItem, 'children'> {
  order: number;
  children?: VisibleNavItem[];
}
export interface VisibleNavSection extends Omit<NavSection, 'items'> {
  order: number;
  items: VisibleNavItem[];
}

interface Access {
  can: (...perms: Permission[]) => boolean;
  superAdmin: boolean;
}

/** Apply brand overlays (by id, at any depth), then permissions. Sections left empty disappear. */
export function visibleNav(
  overlays: BrandNavItemOverlay[],
  access: Access,
  nav: NavSection[] = NAV,
): VisibleNavSection[] {
  const byId = new Map<string, { label?: string; icon?: string; hidden?: boolean; order?: number }>();
  for (const o of overlays) {
    byId.set(o.id, o);
    for (const c of o.children ?? []) byId.set(c.id, c);
  }
  const allowed = (item: NavItem) =>
    !byId.get(item.id)?.hidden && (!item.superAdminOnly || access.superAdmin) && access.can(...item.permissions);
  const present = (item: NavItem, i: number): VisibleNavItem => {
    const o = byId.get(item.id);
    const icon = o?.icon ? OVERLAY_ICONS[o.icon] : undefined;
    const children = (item.children ?? [])
      .filter(allowed)
      .map(present)
      .sort((a, b) => a.order - b.order);
    return {
      ...item,
      label: o?.label ?? item.label,
      icon: icon ?? item.icon,
      order: o?.order ?? i,
      children: children.length ? children : undefined,
    };
  };
  const sections: VisibleNavSection[] = [];
  nav.forEach((section, sectionIndex) => {
    const so = byId.get(section.id);
    if (so?.hidden) return;
    const items = section.items
      .filter(allowed)
      .map(present)
      .sort((a, b) => a.order - b.order);
    if (!items.length) return;
    const icon = so?.icon ? OVERLAY_ICONS[so.icon] : undefined;
    sections.push({
      id: section.id,
      label: so?.label ?? section.label,
      icon: icon ?? section.icon,
      order: so?.order ?? sectionIndex,
      items,
    });
  });
  return sections.sort((a, b) => a.order - b.order);
}

/** Every item, sub-items included. */
export const allNavItems = (nav: NavSection[] = NAV): NavItem[] =>
  nav.flatMap((s) => s.items.flatMap((i) => [i, ...(i.children ?? [])]));

/** The nav entry that owns a path (longest matching `to`), if any. */
export function findNavItem(pathname: string, nav: NavSection[] = NAV): NavItem | undefined {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return allNavItems(nav)
    .filter((i) => clean === i.to || clean.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/** Where an item sits: its section and, for a sub-item, its parent. */
export function navTrail(
  item: NavItem,
  nav: NavSection[] = NAV,
): { section: NavSection; parent?: NavItem } | undefined {
  for (const section of nav) {
    for (const top of section.items) {
      if (top.id === item.id) return { section };
      if (top.children?.some((c) => c.id === item.id)) return { section, parent: top };
    }
  }
  return undefined;
}

/** Brand `i18n.pageTitles`: exact path first, else the longest `/`-prefix; otherwise the fallback. */
export function resolvePageTitle(pathname: string, pageTitles: Record<string, string>, fallback: string): string {
  if (pageTitles[pathname]) return pageTitles[pathname];
  const prefix = Object.keys(pageTitles)
    .filter((p) => pathname.startsWith(p.endsWith('/') ? p : `${p}/`))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? (pageTitles[prefix] ?? fallback) : fallback;
}
